import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Cpu, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { isExtensionInstalled } from "@/lib/extension";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function ExtensionStatusIndicator() {
  const [status, setStatus] = useState<"checking" | "installed" | "not_installed">("checking");
  const navigate = useNavigate();

  useEffect(() => {
    // Small delay to allow content script injection
    const timer = setTimeout(() => {
      setStatus(isExtensionInstalled() ? "installed" : "not_installed");
    }, 1000);

    // Listen for extension ready message if it sends one
    const handler = (event: MessageEvent) => {
      if (event.source !== window) return;
      if (event.data?.type === "NIPSYS_EXTENSION_READY") {
        setStatus("installed");
      }
    };

    window.addEventListener("message", handler);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("message", handler);
    };
  }, []);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium transition-all",
              status === "installed"
                ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                : status === "not_installed"
                ? "bg-amber-50 text-amber-600 border border-amber-100 cursor-pointer hover:bg-amber-100"
                : "bg-slate-50 text-slate-400 border border-slate-100 cursor-help"
            )}
            onClick={status === "not_installed" ? () => navigate("/geral/extensao") : undefined}
          >
            <Cpu className={cn("w-3 h-3", status === "installed" && "animate-pulse")} />
            <span className="uppercase tracking-wider">
              NIP Extension
            </span>
            {status === "installed" ? (
              <CheckCircle2 className="w-2.5 h-2.5" />
            ) : status === "not_installed" ? (
              <AlertCircle className="w-2.5 h-2.5" />
            ) : null}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs max-w-[200px]">
          {status === "installed" ? (
            <p>Extensão NIPSYS Turbo ativa. Scraping direto habilitado.</p>
          ) : status === "not_installed" ? (
            <p>Extensão não detectada. Clique aqui para instalar (download).</p>
          ) : (
            <p>Verificando presença da extensão...</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
